import React, { createContext, FC, useCallback, useContext, useState } from "react";
import { AutovioCalendarEvent } from "../model/autovioCalendarEvents";
import { serverAPI } from "../api/server.api";

const EventDialogContext = createContext<{
  isSaving: boolean;
  studentsTempAttendance: Record<string, boolean>;
  handleAttendButtonClick: (studentId: string) => void;
  handleNoShowButtonClick: (studentId: string) => void;
  handleSaveStudentsAttendance: () => Promise<void>;
} | null>(null);

export const EventDialogProvider: FC<{ children: React.ReactNode; event: AutovioCalendarEvent }> = ({
  children,
  event,
}) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [studentsTempAttendance, setStudentsTempAttendance] = useState<Record<string, boolean>>({});

  const handleAttendButtonClick = useCallback(
    (studentId: string) => {
      if (isSaving) return;
      const attended = studentsTempAttendance[studentId] === true;
      if (attended) return;
      setStudentsTempAttendance((prevState) => ({ ...prevState, [studentId]: true }));
    },
    [isSaving, studentsTempAttendance],
  );

  const handleNoShowButtonClick = useCallback(
    (studentId: string) => {
      if (isSaving) return;
      const noShow = studentsTempAttendance[studentId] === false;
      if (noShow) return;
      setStudentsTempAttendance((prevState) => ({ ...prevState, [studentId]: false }));
    },
    [isSaving, studentsTempAttendance],
  );

  const handleSaveStudentsAttendance = useCallback(async () => {
    setIsSaving(true);
    await runSafely(
      () =>
        serverAPI.updateTheoryLessonAttendance({
          eventId: event.id,
          attendance: studentsTempAttendance,
        }),
      {
        errorMessage: "Fehler beim Speichern der Anwesenheit.",
        successMessage: "Anwesenheit erfolgreich gespeichert.",
        onSuccess: () => setStudentsTempAttendance({}),
      },
    );
    setIsSaving(false);
  }, [event.id, studentsTempAttendance]);

  return (
    <EventDialogContext.Provider
      value={{
        isSaving,
        studentsTempAttendance,
        handleAttendButtonClick,
        handleNoShowButtonClick,
        handleSaveStudentsAttendance,
      }}
    >
      {children}
    </EventDialogContext.Provider>
  );
};

export const useEventDialogContext = () => {
  const context = useContext(EventDialogContext);
  if (!context) {
    throw new Error("useEventDialogContext must be used within an EventDialogProvider");
  }
  return context;
};
